import { z } from 'zod';
import {
  AlcoholHabit,
  ConvertStatus,
  DealBreakerTag,
  Diet,
  DressStyleMan,
  DressStyleWoman,
  EducationLevel,
  FamilyInvolvement,
  LivingArrangement,
  MaritalStatus,
  MarriageTimeframe,
  PolygamyPreference,
  PrayerFrequency,
  QuranEngagement,
  Relocation,
  SearchMode,
  Sect,
  SmokingHabit,
  SubSect,
  WantsChildren,
} from '../enums.js';
import { MIN_AGE_YEARS } from '../helpers/age.js';
import { Iso3CountrySchema, IsoLanguageSchema, UuidSchema } from '../primitives.js';

/** Union of every dress-style value across both genders. The drawer
 *  picks the gender-appropriate target set based on `viewer.gender`
 *  (a male viewer filters on `DressStyleWoman`, a female viewer
 *  filters on `DressStyleMan`). The wire format stays a single
 *  combined enum so the schema doesn't need to know about gender. */
export const DressStyleValue = z.enum([
  // DressStyleWoman
  'niqab',
  'jilbab',
  'hijab',
  'modest_no_hijab',
  // DressStyleMan
  'thobe_regular',
  'thobe_occasional',
  'beard_full',
  'beard_trim',
  // Both
  'casual',
  'dont_dress_religiously',
  'prefer_not_say',
]);
export type DressStyleValue = z.infer<typeof DressStyleValue>;

// All eight gendered values + the three shared values. The matcher
// resolves the candidate's `dressStyle` (gender-specific enum value)
// against the viewer's `dressStyleAcceptable[]` directly — no further
// translation needed because the values are disjoint by gender
// except for `casual` / `dont_dress_religiously` / `prefer_not_say`.

/**
 * Partner preferences. 1:1 with User. Editable from Settings post-wizard.
 *
 * Age window must be ≥18 on both ends regardless of UI — server enforces.
 * Empty list semantics: empty `countriesAcceptable` means "any country";
 * empty `sectAcceptable` would mean "no sect acceptable" which is invalid,
 * so we require at least one sect.
 */
export const PreferencesSchema = z
  .object({
    userId: UuidSchema,

    // Age
    ageMin: z.number().int().min(MIN_AGE_YEARS).max(99),
    ageMax: z.number().int().min(MIN_AGE_YEARS).max(99),

    // Geography
    searchMode: SearchMode.default('distance_or_countries'),
    distanceKmMax: z.number().int().min(1).max(20000).nullable(),
    countriesAcceptable: z.array(Iso3CountrySchema).max(50),

    // Religion
    sectAcceptable: z.array(Sect).min(1),
    /** Sub-sects / madhhabs the user accepts from a candidate.
     *  Mutual hard-filter (same pattern as livingArrangementAcceptable).
     *  Default-permissive: all 12 enum values, including `na` so that
     *  candidates who didn't disclose aren't filtered out unless the
     *  viewer explicitly tightens. Users who care about madhhab
     *  alignment narrow to a subset (e.g. just `hanafi` or all four
     *  Sunni schools). */
    subSectAcceptable: z.array(SubSect).min(1),

    // Demographics
    nationalityPref: z.array(Iso3CountrySchema).max(50),
    ethnicityPref: z.array(z.string().min(1).max(40)).max(20),
    languageRequired: z.array(IsoLanguageSchema).max(8),

    // Lifestyle / status
    educationMin: EducationLevel.nullable(),
    maritalStatusAcceptable: z.array(MaritalStatus).min(1),
    hasChildrenAcceptable: z.enum(['ok', 'not_ok', 'only_if']),
    wantsChildrenRequired: z.array(WantsChildren).min(1),
    relocationRequired: z.array(Relocation).min(1),
    /** Marriage-timeframe acceptable. Same mutual-compatibility shape
     *  as wantsChildrenRequired + relocationRequired — both sides have
     *  to be on the same page about timing for matrimonial to work.
     *  Default is the permissive full set; `.min(1)` keeps the filter
     *  from going empty by accident. */
    marriageTimeframeAcceptable: z.array(MarriageTimeframe).min(1),

    // Physical
    /**
     * Height range filter — both bounds nullable for "no preference."
     * cm units; valid range matches `ProfileSchema.heightCm` (120-230).
     * Matcher applies as a mutual hard filter (same shape as the age
     * window): viewer's height must be in target's range AND target's
     * height must be in viewer's range. Either side can be NULL to
     * indicate that side of the range is open.
     */
    heightMinCm: z.number().int().min(120).max(230).nullable(),
    heightMaxCm: z.number().int().min(120).max(230).nullable(),

    /**
     * Dress-style preference. One-way filter (the matcher checks
     * candidate.dressStyle ∈ viewer.dressStyleAcceptable; the
     * candidate doesn't get a symmetric say on the viewer's dress
     * style — your own dress style is a personal practice, not
     * something a partner should be filtering on you for. The
     * cross-direction case is the viewer's OWN dress style which
     * THEY already control via their profile.). Default-permissive:
     * every dress-style value plus `prefer_not_say` so existing rows
     * survive the migration unfiltered.
     */
    dressStyleAcceptable: z.array(DressStyleValue).min(1),

    /**
     * Smoking + alcohol acceptability. One-way filters (candidate's
     * habit ∈ viewer's acceptable set). Graded — `quit_ok` etc. is
     * not modeled as a separate enum; we use the existing
     * `SmokingHabit` / `AlcoholHabit` values directly. Default-
     * permissive: all values acceptable.
     *
     * The graded `DealBreakerTag` ('smokes' / 'drinks_alcohol') is
     * a parallel hard exclude — these arrays are a finer-grain
     * filter that survives when the deal-breaker tag is off.
     */
    smokingAcceptable: z.array(SmokingHabit).min(1),
    alcoholAcceptable: z.array(AlcoholHabit).min(1),

    /**
     * Convert / revert acceptance. One-way filter on candidate's
     * `convertStatus`. Most users are open; a meaningful subset
     * wants `born_muslim_only` (especially in some MENA / South
     * Asian contexts) and a smaller cohort wants `revert_only`
     * (e.g. some convert-only communities). Default-permissive.
     */
    convertStatusAcceptable: z.array(ConvertStatus).min(1),
    /** Diet acceptable. One-way filter (parallel to alcohol /
     *  smoking / dress-style) — candidate's diet must be in this
     *  set. Halal-strict users narrow to `['halal_strict']` to see
     *  only halal-strict candidates. Default is the permissive full
     *  set so an unset preference matches anyone. */
    dietAcceptable: z.array(Diet).min(1),
    /** Grew-up-country preference (ISO-3 codes). Distinct from
     *  nationalityPref + countriesAcceptable — this is the
     *  cultural-upbringing signal (where they spent formative
     *  years). Empty array = no preference (matches anyone). One-
     *  way filter: candidate.grewUpCountryIso ∈ this set when set. */
    grewUpCountryAcceptable: z.array(Iso3CountrySchema).max(50),

    /**
     * Family-involvement timeframe acceptance. Mutual hard filter
     * (same pattern as wantsChildrenRequired + relocationRequired) —
     * candidate's `familyInvolvement` ∈ viewer's acceptable set AND
     * viewer's `familyInvolvement` ∈ candidate's. Users who chose
     * "involve family immediately" often want only matches who
     * also picked `immediately` or `after_we_connect` — the early
     * stages of a serious matrimonial conversation drift very
     * differently between the two pictures. Default-permissive.
     */
    familyInvolvementAcceptable: z.array(FamilyInvolvement).min(1),

    /** Living arrangements the user accepts from a candidate.
     *  Mutual hard-filter in the matcher (same pattern as
     *  relocationRequired). */
    livingArrangementAcceptable: z.array(LivingArrangement).min(1),
    /** Polygamy stances the user accepts from a candidate. Same
     *  mutual hard-filter pattern. `prefer_not_to_say` is the
     *  only opt-out: if a viewer keeps it in their acceptable set
     *  they're willing to consider undisclosed candidates. */
    polygamyAcceptable: z.array(PolygamyPreference).min(1),
    religiosityMin: z.number().int().min(1).max(5),

    /**
     * Prayer frequency acceptable. One-way hard filter. Default
     * `.min(1)` enforces non-empty; "no preference" is expressed as
     * "all enum values selected" and find.tsx collapses that to an
     * empty UI selection. Matches the candidate's `prays` field.
     */
    praysAcceptable: z.array(PrayerFrequency).min(1),
    /**
     * Quran engagement acceptable. Same shape as prays. Matches the
     * candidate's `quranEngagement` field.
     */
    quranEngagementAcceptable: z.array(QuranEngagement).min(1),
    /**
     * Mother tongue required. Empty array = no preference (matches
     * any candidate). When non-empty, the candidate's `motherTongue`
     * must be in the set. The candidate-side field is nullable; we
     * treat a NULL candidate motherTongue as not-matching when the
     * viewer has narrowed, matching the conservative rule applied
     * elsewhere.
     */
    motherTongueRequired: z.array(IsoLanguageSchema).max(8).default([]),
    /**
     * Interests required (ANY-match). Empty array = no preference.
     * When non-empty, the candidate must declare AT LEAST ONE
     * interest in the viewer's set (jsonb array overlap). Free-form
     * tags, capped at 15 to match the candidate-side limit.
     */
    interestsRequired: z.array(z.string().trim().min(1).max(30)).max(15).default([]),
    /**
     * Personality traits required (ANY-match). Same shape as
     * interests. Capped at 10 to match the candidate-side limit.
     */
    personalityTraitsRequired: z.array(z.string().trim().min(1).max(30)).max(10).default([]),

    // Flexible bag for additional deal-breakers we add over time
    dealbreakers: z.record(z.string(), z.unknown()).default({}),
    /** Audit Spec C7 — first-class deal-breaker tags. Each tag maps
     *  to a candidate-trait check in the matcher (see
     *  `dealBreakerExcludes()` in @funkihoo/shared). Default empty
     *  list means no deal-breaker filtering — the broad mutual fields
     *  above (sect_acceptable, marital_status_acceptable, etc.)
     *  already do the major gating. Adding tags here is a fine-grain
     *  exclude on top. */
    dealBreakerTags: z.array(DealBreakerTag).default([]),
  })
  .refine((data) => data.ageMin <= data.ageMax, {
    message: 'ageMin must be ≤ ageMax',
    path: ['ageMin'],
  })
  .refine(
    (data) =>
      data.heightMinCm === null ||
      data.heightMaxCm === null ||
      data.heightMinCm <= data.heightMaxCm,
    {
      message: 'heightMinCm must be ≤ heightMaxCm',
      path: ['heightMinCm'],
    },
  );
export type Preferences = z.infer<typeof PreferencesSchema>;

// ─── Helpers ──────────────────────────────────────────────────────────

/**
 * The full default-permissive value set for each enum-array preference.
 * The drawer uses these to translate UI "empty = no preference" into
 * the wire format's `.min(1)` requirement, and to detect when a
 * preference is effectively unfiltered (every value selected).
 */
export const DRESS_STYLE_VALUES = DressStyleValue.options;
export const SMOKING_HABIT_VALUES = SmokingHabit.options;
export const ALCOHOL_HABIT_VALUES = AlcoholHabit.options;
export const CONVERT_STATUS_VALUES = ConvertStatus.options;
export const DIET_VALUES = Diet.options;
export const FAMILY_INVOLVEMENT_VALUES = FamilyInvolvement.options;
export const MARRIAGE_TIMEFRAME_VALUES = MarriageTimeframe.options;
export const PRAYER_FREQUENCY_VALUES = PrayerFrequency.options;
export const QURAN_ENGAGEMENT_VALUES = QuranEngagement.options;

/**
 * Gender-aware dress-style options for the FILTER side. A male viewer
 * filtering for a female candidate sees the woman enum + the shared
 * three values; a female viewer filtering for a male candidate sees
 * the man enum + the shared three. `prefer_not_say` always appears
 * so users can include candidates who didn't disclose.
 */
export function dressStyleOptionsForTarget(targetGender: 'male' | 'female'): DressStyleValue[] {
  const shared: DressStyleValue[] = ['casual', 'dont_dress_religiously', 'prefer_not_say'];
  if (targetGender === 'female') {
    return [
      ...DressStyleWoman.options.filter((v) => !shared.includes(v as DressStyleValue)),
      ...shared,
    ];
  }
  return [
    ...DressStyleMan.options.filter((v) => !shared.includes(v as DressStyleValue)),
    ...shared,
  ];
}
